import { useState, MouseEvent } from 'react';

import { ListItemText } from '@mui/material';
import ListSubheader from '@mui/material/ListSubheader';
import Menu from '@mui/material/Menu';
import scssVariables from '@styles/variables.module.scss';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router';

import { CreateMenuCategory } from './types';
import { useGetUserMe } from '@/api/REST/userMe';
import { DefaultMenuItem } from '@/components/ui/DefaultMenuItem';
import { Icon } from '@/components/ui/Icon';
import { IconButton } from '@/components/ui/IconButton';
import { areaIcons } from '@/constants/areaIcons';
import { createMenuItems } from '@/constants/createMenuItems';
import { uiIcons } from '@/constants/uiIcons';
import { useHasNewProjectManagement } from '@/hooks/featureFlags';
import { useNewProjectModalStore } from '@/stores/newProjectModal';

export const CreateMenu = () => {
  const { t } = useTranslation('header');
  const openNewProjectModal = useNewProjectModalStore((state) => state.open);
  const hasNewProjectManagement = useHasNewProjectManagement();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleOpenNewProjectModal = () => {
    handleClose();
    openNewProjectModal();
  };

  const { data: userMeData, isLoading } = useGetUserMe();

  const filterCreateMenuItems = (items: CreateMenuCategory[]) => {
    if (isLoading || !userMeData) {
      return [];
    }

    const userPermissions =
      userMeData.permittedPages?.map(
        (permission) => permission.pageIdentifier,
      ) ?? [];

    return items
      .map((categoryItem) => ({
        ...categoryItem,
        items: categoryItem.items
          .filter((item) => userPermissions.includes(item.requiredPermission))
          .filter(
            (item) =>
              !(
                item.name === 'project_quick_create' && hasNewProjectManagement
              ),
          ),
      }))
      .filter((categoryItem) => categoryItem.items.length > 0);
  };

  const filteredCreateMenuItems = filterCreateMenuItems(createMenuItems);

  return filteredCreateMenuItems.length > 0 ? (
    <>
      <IconButton
        size="small"
        id="create-menu-button"
        color="secondary"
        aria-controls={open ? 'create-menu' : undefined}
        aria-haspopup="menu"
        aria-expanded={open}
        selected={open}
        disabled={isLoading || !userMeData}
        onClick={handleOpen}
        data-automation-id="TopBarCreateButton"
        iconName={areaIcons.actions}
        tooltip={t('CreateMenuTooltip')}
        iconProps={{ color: scssVariables.ColorBlack100 }}
      />
      <Menu
        id="create-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          list: {
            component: 'nav',
            'aria-labelledby': 'create-menu-button',
          },
        }}
      >
        {filteredCreateMenuItems.flatMap((category) => [
          <ListSubheader
            key={`header-${category.name}`}
            disableGutters
            component="div"
          >
            {t(category.label)}
          </ListSubheader>,
          ...category.items.map((item) =>
            item.name === 'project' && hasNewProjectManagement ? (
              <DefaultMenuItem
                key={item.name}
                component={NavLink}
                onClick={handleOpenNewProjectModal}
                data-automation-id={`create-menu-item-${item.name}`}
              >
                <Icon
                  name={uiIcons.actions.add}
                  aria-hidden="true"
                  size="tiny"
                />
                <ListItemText>{t(item.label)}</ListItemText>
              </DefaultMenuItem>
            ) : (
              <DefaultMenuItem
                key={item.name}
                component={NavLink}
                to={item.path}
                onClick={handleClose}
                data-automation-id={`create-menu-item-${item.name}`}
              >
                <Icon
                  name={uiIcons.actions.add}
                  aria-hidden="true"
                  size="tiny"
                />
                <ListItemText>{t(item.label)}</ListItemText>
              </DefaultMenuItem>
            ),
          ),
        ])}
      </Menu>
    </>
  ) : null;
};
